import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  BookOpen, ChevronDown, ChevronUp, AlertTriangle, Info, 
  Scale, Shield, CloudRain, Bug, Train, Zap, Mountain, 
  Tent, Package, Home, MapPin, ExternalLink, Plane, Hotel
} from 'lucide-react';
import './GuidePage.css';

export default function GuidePage() {
  const { t } = useTranslation();
  const [openSections, setOpenSections] = useState(['overview']);

  const toggleSection = (id) => {
    setOpenSections(prev => 
      prev.includes(id) 
        ? prev.filter(s => s !== id)
        : [...prev, id]
    );
  };

  const sections = [
    {
      id: 'overview',
      title: t('guide.overview.title'),
      icon: <Info size={24} />,
      content: (
        <div className="guide-content">
          <div className="alert-box info">
            <AlertTriangle size={20} />
            <div>
              <strong>{t('guide.overview.important')}:</strong> {t('guide.overview.drive_left')}
            </div>
          </div>
          
          <h3>{t('guide.overview.best_season_title')}</h3>
          <ul>
            <li><strong>{t('guide.overview.best_season_optimal')}</strong></li>
            <li>{t('guide.overview.best_season_summer')}</li>
            <li>{t('guide.overview.best_season_west')}</li>
            <li>{t('guide.overview.best_season_wind')}</li>
          </ul>

          <div className="tip-box">
            <Info size={18} />
            <p>{t('guide.overview.tip_text')}</p>
          </div>
        </div>
      )
    },
    {
      id: 'legal',
      title: t('guide.legal.title'),
      icon: <Scale size={24} />,
      content: (
        <div className="guide-content">
          <h3>{t('guide.legal.ebike_title')}</h3>
          <ul>
            <li>✓ {t('guide.legal.ebike_power')}</li>
            <li>✓ {t('guide.legal.ebike_speed')}</li>
            <li>✓ {t('guide.legal.ebike_age')}</li>
            <li>✓ {t('guide.legal.ebike_license')}</li>
          </ul>

          <h3>{t('guide.legal.equipment_title')}</h3>
          <ul>
            <li><strong>{t('guide.legal.equipment_lights')}</strong></li>
            <li><strong>{t('guide.legal.equipment_reflectors')}</strong></li>
            <li><strong>{t('guide.legal.equipment_helmet')}</strong></li>
          </ul>

          <h3>{t('guide.legal.access_title')}</h3>
          <p>{t('guide.legal.access_text')}</p>
          
          <a href="https://www.outdooraccess-scotland.scot" target="_blank" rel="noopener noreferrer" className="external-link">
            <ExternalLink size={16} />
            {t('guide.legal.access_link')}
          </a>
        </div>
      )
    },
    {
      id: 'safety',
      title: t('guide.safety.title'),
      icon: <Shield size={24} />,
      content: (
        <div className="guide-content">
          <div className="alert-box warning">
            <AlertTriangle size={20} />
            <div>
              <strong>{t('guide.safety.single_track_warning')}</strong>
            </div>
          </div>

          <h3>{t('guide.safety.ticks_title')}</h3>
          <ul>
            <li>{t('guide.safety.ticks_clothing')}</li>
            <li>{t('guide.safety.ticks_check')}</li>
            <li>{t('guide.safety.ticks_removal')}</li>
            <li>{t('guide.safety.ticks_symptoms')}</li>
          </ul>

          <div className="tip-box">
            <Info size={18} />
            <p><strong>{t('guide.safety.emergency_title')}:</strong> {t('guide.safety.emergency_text')}</p>
          </div>

          <h3>{t('guide.safety.traffic_title')}</h3>
          <ul>
            <li>{t('guide.safety.traffic_distance')}</li>
            <li>{t('guide.safety.traffic_lights')}</li>
            <li>{t('guide.safety.traffic_groups')}</li>
          </ul>
        </div>
      )
    },
    {
      id: 'weather',
      title: t('guide.weather.title'),
      icon: <CloudRain size={24} />,
      content: (
        <div className="guide-content">
          <h3>{t('guide.weather.temp_title')}</h3>
          <ul>
            <li><strong>{t('guide.weather.temp_warmest')}</strong></li>
            <li><strong>{t('guide.weather.temp_east')}</strong></li>
            <li><strong>{t('guide.weather.temp_west')}</strong></li>
          </ul>

          <div className="alert-box warning">
            <Bug size={20} />
            <div>
              <strong>{t('guide.weather.midges_warning')}</strong>
              <ul>
                <li>{t('guide.weather.midges_season')}</li>
                <li>{t('guide.weather.midges_worst')}</li>
                <li>{t('guide.weather.midges_protection')}</li>
              </ul>
            </div>
          </div>

          <a href="https://www.smidgeup.com/midge-forecast" target="_blank" rel="noopener noreferrer" className="external-link">
            <ExternalLink size={16} />
            {t('guide.weather.midges_forecast')}
          </a>
        </div>
      )
    },
    {
      id: 'transport',
      title: t('guide.transport.title'),
      icon: <Train size={24} />,
      content: (
        <div className="guide-content">
          <h3>{t('guide.transport.train_title')}</h3>
          <ul>
            <li>✓ {t('guide.transport.train_bikes_free')}</li>
            <li>✓ {t('guide.transport.train_ebikes')}</li>
            <li>✗ {t('guide.transport.train_scooters')}</li>
            <li>{t('guide.transport.train_booking')}</li>
          </ul>

          <h3>{t('guide.transport.ferry_title')}</h3>
          <p><strong>{t('guide.transport.ferry_calmac')}</strong></p>
          <ul>
            <li>{t('guide.transport.ferry_calmac_bikes')}</li>
            <li>{t('guide.transport.ferry_calmac_booking')}</li>
            <li>{t('guide.transport.ferry_calmac_spaces')}</li>
          </ul>

          <p><strong>{t('guide.transport.ferry_northlink')}</strong></p>
          <ul>
            <li>{t('guide.transport.ferry_northlink_booking')}</li>
          </ul>

          <h3>{t('guide.transport.bus_title')}</h3>
          <ul>
            <li>{t('guide.transport.bus_storage')}</li>
            <li>{t('guide.transport.bus_sock')}</li>
            <li>{t('guide.transport.bus_limited')}</li>
            <li>{t('guide.transport.bus_ebikes')}</li>
          </ul>

          <div className="alert-box warning">
            <AlertTriangle size={20} />
            <div>
              <strong>{t('guide.transport.flight_warning')}</strong>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'charging',
      title: t('guide.charging.title'),
      icon: <Zap size={24} />,
      content: (
        <div className="guide-content">
          <h3>{t('guide.charging.public_title')}</h3>
          <p>{t('guide.charging.public_text')}</p>

          <div className="tip-box">
            <Info size={18} />
            <div>
              <strong>{t('guide.charging.tips_title')}</strong>
              <ul>
                <li>{t('guide.charging.tips_charger')}</li>
                <li>{t('guide.charging.tips_accommodation')}</li>
                <li>{t('guide.charging.tips_plug')}</li>
                <li>{t('guide.charging.tips_powerbank')}</li>
              </ul>
            </div>
          </div>

          <h3>{t('guide.charging.range_title')}</h3>
          <ul>
            <li>❄️ {t('guide.charging.range_cold')}</li>
            <li>💨 {t('guide.charging.range_wind')}</li>
            <li>🏔️ {t('guide.charging.range_terrain')}</li>
          </ul>

          <p><strong>{t('guide.charging.recommendation')}</strong></p>
        </div>
      )
    },
    {
      id: 'camping',
      title: t('guide.camping.title'),
      icon: <Tent size={24} />,
      content: (
        <div className="guide-content">
          <h3>{t('guide.camping.wild_title')}</h3>
          <p>{t('guide.camping.wild_intro')}</p>
          <ul>
            <li>{t('guide.camping.wild_distance')}</li>
            <li>{t('guide.camping.wild_trace')}</li>
            <li>{t('guide.camping.wild_code')}</li>
            <li>{t('guide.camping.wild_fire')}</li>
            <li>{t('guide.camping.wild_trash')}</li>
          </ul>

          <div className="alert-box warning">
            <AlertTriangle size={20} />
            <div>
              <strong>{t('guide.camping.lomond_warning')}</strong>
            </div>
          </div>

          <h3>{t('guide.camping.bothies_title')}</h3>
          <p>{t('guide.camping.bothies_intro')}</p>
          <ul>
            <li>{t('guide.camping.bothies_groups')}</li>
            <li>{t('guide.camping.bothies_clean')}</li>
            <li>{t('guide.camping.bothies_respect')}</li>
            <li>{t('guide.camping.bothies_no_booking')}</li>
          </ul>

          <a href="https://www.mountainbothies.org.uk" target="_blank" rel="noopener noreferrer" className="external-link">
            <ExternalLink size={16} />
            {t('guide.camping.bothies_link')}
          </a>
        </div>
      )
    },
    {
      id: 'routes',
      title: t('guide.routes.title'),
      icon: <Mountain size={24} />,
      content: (
        <div className="guide-content">
          <div className="route-card">
            <h3>🏝️ {t('guide.routes.hebridean_title')}</h3>
            <p><strong>{t('guide.routes.hebridean_stats')}</strong></p>
            <p>{t('guide.routes.hebridean_desc')}</p>
          </div>

          <div className="route-card">
            <h3>🏔️ {t('guide.routes.caledonia_title')}</h3>
            <p><strong>{t('guide.routes.caledonia_stats')}</strong></p>
            <p>{t('guide.routes.caledonia_desc')}</p>
          </div>

          <div className="route-card">
            <h3>🚂 {t('guide.routes.deeside_title')}</h3>
            <p><strong>{t('guide.routes.deeside_stats')}</strong></p>
            <p>{t('guide.routes.deeside_desc')}</p>
          </div>

          <div className="route-card">
            <h3>🥃 {t('guide.routes.speyside_title')}</h3>
            <p><strong>{t('guide.routes.speyside_stats')}</strong></p>
            <p>{t('guide.routes.speyside_desc')}</p>
          </div>

          <h3>{t('guide.routes.mtb_title')}</h3>
          <ul>
            <li><strong>{t('guide.routes.mtb_glentress')}</strong></li>
            <li><strong>{t('guide.routes.mtb_nevis')}</strong></li>
            <li><strong>{t('guide.routes.mtb_7stanes')}</strong></li>
          </ul>
        </div>
      )
    },
    {
      id: 'equipment',
      title: t('guide.equipment.title'),
      icon: <Package size={24} />,
      content: (
        <div className="guide-content">
          <h3>{t('guide.equipment.tires_title')}</h3>
          <ul>
            <li><strong>{t('guide.equipment.tires_tour')}</strong></li>
            <li><strong>{t('guide.equipment.tires_gravel')}</strong></li>
          </ul>

          <h3>{t('guide.equipment.weather_title')}</h3>
          <ul>
            <li>✓ {t('guide.equipment.weather_jacket')}</li>
            <li>✓ {t('guide.equipment.weather_overshoes')}</li>
            <li>✓ {t('guide.equipment.weather_gloves')}</li>
            <li>✓ {t('guide.equipment.weather_bags')}</li>
          </ul>

          <h3>{t('guide.equipment.electronics_title')}</h3>
          <ul>
            <li>✓ {t('guide.equipment.electronics_lights')}</li>
            <li>✓ {t('guide.equipment.electronics_charger')}</li>
            <li>✓ {t('guide.equipment.electronics_gps')}</li>
          </ul>

          <h3>{t('guide.equipment.ebike_title')}</h3>
          <ul>
            <li>✓ {t('guide.equipment.ebike_charger')}</li>
            <li>✓ {t('guide.equipment.ebike_cold')}</li>
            <li>✓ {t('guide.equipment.ebike_spare')}</li>
            <li>✓ {t('guide.equipment.ebike_insulation')}</li>
          </ul>

          <h3>{t('guide.equipment.health_title')}</h3>
          <ul>
            <li>✓ {t('guide.equipment.health_net')}</li>
            <li>✓ {t('guide.equipment.health_repellent')}</li>
            <li>✓ {t('guide.equipment.health_tick')}</li>
            <li>✓ {t('guide.equipment.health_first_aid')}</li>
          </ul>
        </div>
      )
    },
    {
      id: 'planning',
      title: t('guide.planning.title'),
      icon: <Hotel size={24} />,
      content: (
        <div className="guide-content">
          {/* Accommodation Section */}
          <h3>🏨 {t('guide.planning.accommodation_title')}</h3>
          
          <h4>{t('guide.planning.accommodation_best')}</h4>
          <div className="platform-grid">
            <div className="platform-card">
              <h5>✅ {t('guide.planning.hostelworld_title')}</h5>
              <p>{t('guide.planning.hostelworld_desc')}</p>
              <a href="https://www.hostelworld.com" target="_blank" rel="noopener noreferrer" className="external-link">
                <ExternalLink size={16} />
                {t('guide.planning.visit_website')}
              </a>
            </div>
            
            <div className="platform-card">
              <h5>✅ {t('guide.planning.hostelling_title')}</h5>
              <p>{t('guide.planning.hostelling_desc')}</p>
              <a href="https://www.hostellingscotland.org.uk" target="_blank" rel="noopener noreferrer" className="external-link">
                <ExternalLink size={16} />
                {t('guide.planning.visit_website')}
              </a>
            </div>
          </div>

          <h4>{t('guide.planning.alternatives_title')}</h4>
          <ul>
            <li>
              <strong>{t('guide.planning.direct_booking_title')}:</strong> {t('guide.planning.direct_booking_desc')}
            </li>
            <li>
              <strong><a href="https://www.hostelz.com" target="_blank" rel="noopener noreferrer">{t('guide.planning.hostelz_title')}</a>:</strong> {t('guide.planning.hostelz_desc')}
            </li>
            <li>
              <strong><a href="https://www.dorms.com" target="_blank" rel="noopener noreferrer">{t('guide.planning.dorms_title')}</a>:</strong> {t('guide.planning.dorms_desc')}
            </li>
          </ul>

          <div className="tip-box">
            <Info size={18} />
            <p>{t('guide.planning.booking_note')}</p>
          </div>

          <h4>📋 {t('guide.planning.workflow_title')}</h4>
          <ol>
            <li>{t('guide.planning.workflow_step1')}</li>
            <li>{t('guide.planning.workflow_step2')}</li>
            <li>{t('guide.planning.workflow_step3')}</li>
          </ol>
          <p><strong>➜ {t('guide.planning.workflow_result')}</strong></p>

          {/* Flights Section */}
          <h3>✈️ {t('guide.planning.flights_title')}</h3>
          
          <h4>{t('guide.planning.flights_best')}</h4>
          <div className="platform-grid">
            <div className="platform-card">
              <h5>✅ {t('guide.planning.skyscanner_title')}</h5>
              <p>{t('guide.planning.skyscanner_desc')}</p>
              <a href="https://www.skyscanner.com" target="_blank" rel="noopener noreferrer" className="external-link">
                <ExternalLink size={16} />
                {t('guide.planning.visit_website')}
              </a>
            </div>
            
            <div className="platform-card">
              <h5>✅ {t('guide.planning.google_flights_title')}</h5>
              <p>{t('guide.planning.google_flights_desc')}</p>
              <a href="https://www.google.com/flights" target="_blank" rel="noopener noreferrer" className="external-link">
                <ExternalLink size={16} />
                {t('guide.planning.visit_website')}
              </a>
            </div>
          </div>

          <ul>
            <li>
              <strong><a href="https://www.kayak.com" target="_blank" rel="noopener noreferrer">KAYAK</a> & <a href="https://www.momondo.com" target="_blank" rel="noopener noreferrer">momondo</a>:</strong> {t('guide.planning.kayak_desc')}
            </li>
            <li>
              <strong>{t('guide.planning.airlines_title')}:</strong> {t('guide.planning.airlines_desc')}<br/>
              {t('guide.planning.airlines_list')}
            </li>
          </ul>

          <h4>📋 {t('guide.planning.flights_workflow_title')}</h4>
          <ol>
            <li>{t('guide.planning.flights_step1')}</li>
            <li>{t('guide.planning.flights_step2')}</li>
            <li>{t('guide.planning.flights_step3')}</li>
          </ol>
        </div>
      )
    },
    {
      id: 'accommodation',
      title: t('guide.accommodation.title'),
      icon: <Home size={24} />,
      content: (
        <div className="guide-content">
          <h3>{t('guide.accommodation.hostels_title')}</h3>
          <p>{t('guide.accommodation.hostels_intro')}</p>
          <ul>
            <li>{t('guide.accommodation.hostels_oban')}</li>
            <li>{t('guide.accommodation.hostels_aviemore')}</li>
            <li>{t('guide.accommodation.hostels_port')}</li>
            <li>{t('guide.accommodation.hostels_lochranza')}</li>
          </ul>
          
          <div className="tip-box">
            <Info size={18} />
            <p>{t('guide.accommodation.hostels_tip')}</p>
          </div>

          <h3>{t('guide.accommodation.networks_title')}</h3>
          <ul>
            <li><strong>{t('guide.accommodation.networks_scottish')}</strong></li>
            <li><strong>{t('guide.accommodation.networks_independent')}</strong></li>
            <li><strong>{t('guide.accommodation.networks_bnb')}</strong></li>
          </ul>

          <a href="https://www.hostellingscotland.org.uk" target="_blank" rel="noopener noreferrer" className="external-link">
            <ExternalLink size={16} />
            {t('guide.accommodation.hostels_link')}
          </a>
        </div>
      )
    }
  ];

  return (
    <div className="guide-page" data-testid="guide-page">
      <header className="page-header">
        <div>
          <h1 className="page-title" data-testid="guide-title">
            <BookOpen size={36} className="inline-icon" />
            {t('guide.page_title')}
          </h1>
          <p className="page-subtitle">{t('guide.page_subtitle')}</p>
        </div>
      </header>

      <div className="guide-sections">
        {sections.map((section) => (
          <div key={section.id} className="guide-section" data-testid={`guide-section-${section.id}`}>
            <button
              className="section-header"
              onClick={() => toggleSection(section.id)}
              data-testid={`section-toggle-${section.id}`}
            >
              <div className="section-title-group">
                <div className="section-icon">{section.icon}</div>
                <h2 className="section-title">{section.title}</h2>
              </div>
              <div className="section-toggle">
                {openSections.includes(section.id) ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
              </div>
            </button>
            
            {openSections.includes(section.id) && (
              <div className="section-content" data-testid={`section-content-${section.id}`}>
                {section.content}
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="guide-footer">
        <div className="tip-box">
          <Info size={20} />
          <p>
            <strong>{t('guide.overview.tip')}:</strong> {t('guide.footer_tip')}
          </p>
        </div>
      </div>
    </div>
  );
}
